import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Target, TrendingUp, AlertCircle } from "lucide-react";

const TITANS_ABBR = "TEN";

function extractAfcSeeds(standings) {
  if (!standings?.children) return { divisions: [], wildcard: [] };
  const afc = standings.children.find((c) => (c.abbreviation || c.name || "").toLowerCase().includes("afc")) || standings.children[0];
  if (!afc?.children) return { divisions: [], wildcard: [] };

  const divisions = afc.children.map((div) => {
    const entries = (div.standings?.entries || []).map((e) => ({
      name: e.team?.displayName,
      abbr: e.team?.abbreviation,
      logo: e.team?.logos?.[0]?.href,
      wins: Number(e.stats?.find((s) => s.name === "wins")?.value ?? 0),
      losses: Number(e.stats?.find((s) => s.name === "losses")?.value ?? 0),
      ties: Number(e.stats?.find((s) => s.name === "ties")?.value ?? 0),
      pct: Number(e.stats?.find((s) => s.name === "winPercent")?.value ?? 0),
      pf: Number(e.stats?.find((s) => s.name === "pointsFor")?.value ?? 0),
      pa: Number(e.stats?.find((s) => s.name === "pointsAgainst")?.value ?? 0),
      streak: e.stats?.find((s) => s.name === "streak")?.displayValue || "",
      divisionName: div.name,
    }));
    entries.sort((a, b) => b.pct - a.pct || (b.wins - a.wins));
    return { name: div.name, entries };
  });

  // Top of each division = #1-4 seeds (sorted by record)
  const divLeaders = divisions
    .map((d) => d.entries[0])
    .filter(Boolean)
    .sort((a, b) => b.pct - a.pct || b.wins - a.wins);

  // Wild card pool: everyone except division leaders
  const leaderAbbrs = new Set(divLeaders.map((t) => t.abbr));
  const wildPool = divisions.flatMap((d) => d.entries).filter((t) => !leaderAbbrs.has(t.abbr));
  wildPool.sort((a, b) => b.pct - a.pct || b.wins - a.wins);
  const wildcard = wildPool.slice(0, 3);
  const inHunt = wildPool.slice(3, 6);

  return { divisions, divLeaders, wildcard, inHunt };
}

function titansSituation(seeds) {
  if (!seeds) return null;
  const allAfc = [...(seeds.divLeaders || []), ...(seeds.wildcard || []), ...(seeds.inHunt || [])];
  const ten = allAfc.find((t) => t?.abbr === TITANS_ABBR);
  if (!ten) return null;
  const idx = allAfc.findIndex((t) => t?.abbr === TITANS_ABBR);
  let bucket = "OUT";
  let detail = "On the outside looking in.";
  let tone = "muted";
  if (idx >= 0 && idx < (seeds.divLeaders?.length || 0)) {
    bucket = "DIVISION";
    detail = "Holding a division seed in the AFC bracket.";
    tone = "good";
  } else if (idx >= 0 && idx < (seeds.divLeaders?.length || 0) + (seeds.wildcard?.length || 0)) {
    bucket = "WILD CARD";
    detail = "In the AFC wild card picture.";
    tone = "good";
  } else if (idx >= 0) {
    bucket = "IN THE HUNT";
    detail = "Outside the bracket — but in striking distance.";
    tone = "warn";
  }
  return { ten, bucket, detail, tone };
}

function SeedRow({ team, seed, highlight }) {
  if (!team) return null;
  const isTitans = team.abbr === TITANS_ABBR;
  return (
    <div
      data-testid={`playoff-seed-${seed}`}
      className={`flex items-center gap-3 px-3 py-2 border-l-4 ${
        isTitans
          ? "border-titans-red bg-titans-red/10"
          : highlight
          ? "border-titans-blue bg-titans-elev/40"
          : "border-transparent"
      }`}
    >
      <div className="font-display text-2xl w-8 text-titans-silver">{seed}</div>
      {team.logo && <img src={team.logo} alt={team.abbr} className="h-8 w-8 object-contain" />}
      <div className="flex-1">
        <div className={`font-display text-base uppercase leading-tight ${isTitans ? "text-white" : "text-titans-silver"}`}>
          {team.name || team.abbr}
        </div>
        <div className="text-[10px] font-cond uppercase tracking-wider text-titans-muted">
          {team.divisionName?.replace(/American Football Conference\s*/i, "AFC ") || ""}
        </div>
      </div>
      <div className={`font-display text-lg ${isTitans ? "text-titans-red" : "text-white"}`}>
        {team.wins}-{team.losses}{team.ties ? `-${team.ties}` : ""}
      </div>
    </div>
  );
}

export default function PlayoffOutlook() {
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/standings");
        setStandings(data);
      } catch {
        toast.error("Standings failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const seeds = useMemo(() => (standings ? extractAfcSeeds(standings) : null), [standings]);
  const titans = useMemo(() => titansSituation(seeds), [seeds]);
  const afcSouth = useMemo(() => seeds?.divisions?.find((d) => /south/i.test(d.name)), [seeds]);

  if (loading) return <div className="text-titans-silver mt-12">Loading playoff picture…</div>;
  if (!standings) return null;

  const toneClasses = {
    good: "bg-titans-blue text-white",
    warn: "bg-yellow-500 text-titans-navy",
    muted: "bg-titans-elev text-titans-silver",
  };

  return (
    <div className="mt-16" data-testid="playoff-outlook">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Postseason Math</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-wide section-title flex items-center gap-3">
            <Trophy size={28} className="text-titans-red" />
            Playoff Outlook
          </h2>
        </div>
      </div>

      {/* Titans situation banner */}
      {titans && (
        <div
          data-testid="titans-playoff-situation"
          className={`titans-card p-5 mb-8 flex flex-col md:flex-row md:items-center gap-4 justify-between`}
          style={{ borderLeft: "6px solid #C8102E" }}
        >
          <div className="flex items-center gap-4">
            <div className={`px-3 py-1 font-cond uppercase tracking-wider text-xs ${toneClasses[titans.tone]}`}>
              {titans.bucket}
            </div>
            <div>
              <div className="font-display text-2xl uppercase leading-tight">
                Titans · {titans.ten.wins}-{titans.ten.losses}{titans.ten.ties ? `-${titans.ten.ties}` : ""}
              </div>
              <div className="text-titans-silver text-sm font-body">{titans.detail}</div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-center">
            <div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Win %</div>
              <div className="font-display text-2xl">{(titans.ten.pct * 100).toFixed(1)}%</div>
            </div>
            <div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Pt Diff</div>
              <div className={`font-display text-2xl ${(titans.ten.pf - titans.ten.pa) >= 0 ? "text-titans-blue" : "text-titans-red"}`}>
                {(titans.ten.pf - titans.ten.pa) >= 0 ? "+" : ""}{titans.ten.pf - titans.ten.pa}
              </div>
            </div>
            {titans.ten.streak && (
              <div>
                <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Streak</div>
                <div className="font-display text-2xl">{titans.ten.streak}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AFC Bracket */}
      <div className="grid lg:grid-cols-2 gap-6" data-testid="afc-bracket">
        <div className="titans-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Target size={16} className="text-titans-red" />
            <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver">Division Seeds (1–4)</div>
          </div>
          <div className="space-y-1">
            {(seeds?.divLeaders || []).slice(0, 4).map((t, i) => (
              <SeedRow key={t.abbr} team={t} seed={i + 1} />
            ))}
          </div>
        </div>
        <div className="titans-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-titans-blue" />
            <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver">Wild Card (5–7)</div>
          </div>
          <div className="space-y-1">
            {(seeds?.wildcard || []).map((t, i) => (
              <SeedRow key={t.abbr} team={t} seed={i + 5} highlight />
            ))}
          </div>
          {(seeds?.inHunt || []).length > 0 && (
            <>
              <div className="flex items-center gap-2 mt-5 mb-2">
                <AlertCircle size={14} className="text-titans-silver" />
                <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver">In the Hunt</div>
              </div>
              <div className="space-y-1">
                {seeds.inHunt.map((t, i) => (
                  <SeedRow key={t.abbr} team={t} seed={i + 8} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Weekly AFC South */}
      {afcSouth && (
        <div className="mt-10" data-testid="weekly-afc-south">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide section-title">
              AFC South · This Week
            </h3>
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
              Updates weekly
            </div>
          </div>
          <div className="titans-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-titans-elev">
                <tr className="text-left font-cond uppercase tracking-wider text-xs text-titans-silver">
                  <th className="px-4 py-3 w-10">#</th>
                  <th className="px-4 py-3">Team</th>
                  <th className="px-4 py-3">W-L-T</th>
                  <th className="px-4 py-3 hidden md:table-cell">PCT</th>
                  <th className="px-4 py-3 hidden md:table-cell">PF</th>
                  <th className="px-4 py-3 hidden md:table-cell">PA</th>
                  <th className="px-4 py-3">Diff</th>
                </tr>
              </thead>
              <tbody>
                {afcSouth.entries.map((t, i) => {
                  const isTen = t.abbr === TITANS_ABBR;
                  const diff = t.pf - t.pa;
                  return (
                    <tr
                      key={t.abbr}
                      data-testid={`afc-south-row-${i}`}
                      className={`border-t border-[rgba(138,141,143,0.15)] ${
                        isTen ? "bg-titans-red/10" : ""
                      }`}
                    >
                      <td className="px-4 py-3 font-display text-xl text-titans-silver">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {t.logo && <img src={t.logo} alt={t.abbr} className="h-7 w-7 object-contain" />}
                          <span className={`font-display text-base md:text-lg uppercase ${isTen ? "text-white" : ""}`}>
                            {t.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-display text-lg">
                        {t.wins}-{t.losses}{t.ties ? `-${t.ties}` : ""}
                      </td>
                      <td className="px-4 py-3 font-cond text-titans-blue hidden md:table-cell">
                        {Number(t.pct).toFixed(3)}
                      </td>
                      <td className="px-4 py-3 font-body hidden md:table-cell">{t.pf}</td>
                      <td className="px-4 py-3 font-body hidden md:table-cell">{t.pa}</td>
                      <td className={`px-4 py-3 font-display ${diff >= 0 ? "text-titans-blue" : "text-titans-red"}`}>
                        {diff >= 0 ? "+" : ""}{diff}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
